import type { AttachmentDto } from '@/api/attachment';
import { narrowEnum, nullableString } from '@/api/mappers/_shared';

/**
 * Family-side attachment view-model. The BE returns the same `AttachmentDto`
 * shape as the patient route, but the family product surface deals with a
 * smaller enum (per PTV2-1032).
 */
export type FamilyDocumentType = 'DOCUMENT' | 'IMAGE' | 'MEDICAL_REPORT' | 'OTHER';

export interface FamilyDocumentViewModel {
  readonly id: string;
  readonly type: FamilyDocumentType;
  readonly fileName: string | null;
  readonly title: string;
  readonly uploadedAt: string | null;
  readonly uploadedBy: string | null;
}

const KNOWN_FAMILY_DOCUMENT_TYPE: ReadonlySet<FamilyDocumentType> =
  new Set<FamilyDocumentType>(['DOCUMENT', 'IMAGE', 'MEDICAL_REPORT', 'OTHER']);

function formatDateObserved(
  value: AttachmentDto['dateObserved'],
): string | null {
  if (value === null || value === undefined) return null;
  const year = typeof value.year === 'number' ? value.year : null;
  if (year === null || year <= 0) return null;
  const parts = [String(year).padStart(4, '0')];
  const month = typeof value.month === 'number' && value.month > 0 ? value.month : null;
  const day = typeof value.day === 'number' && value.day > 0 ? value.day : null;
  if (month !== null) {
    parts.push(String(month).padStart(2, '0'));
    if (day !== null) parts.push(String(day).padStart(2, '0'));
  }
  return parts.join('-');
}

export function mapFamilyDocument(dto: AttachmentDto): FamilyDocumentViewModel {
  const fileName = nullableString(dto.fileName);
  const comments = nullableString(dto.comments);
  return {
    id: dto.id,
    type: narrowEnum<FamilyDocumentType>(dto.type ?? null, KNOWN_FAMILY_DOCUMENT_TYPE, 'OTHER'),
    fileName,
    title: comments ?? fileName ?? '',
    uploadedAt: formatDateObserved(dto.dateObserved),
    uploadedBy: nullableString(dto.source?.label),
  };
}

export function mapFamilyDocuments(
  list: readonly AttachmentDto[] | undefined,
): readonly FamilyDocumentViewModel[] {
  if (list === undefined) return [];
  return list.map(mapFamilyDocument);
}
